/**
 * Queue Ticket Card - Display queue ticket information
 */
import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { QueueStatusBadge } from './QueueStatusBadge';
import { Button } from '@/components/ui/Button';
import { formatWaitTime } from '@/lib/timeUtils';
import type { QueueTicket, Service, Barber } from '@/types';

interface QueueTicketCardProps {
  ticket: QueueTicket;
  onCall?: (queueId: string) => void;
  onMarkArrival?: (queueId: string) => void;
  onComplete?: (queueId: string) => void;
  onCancel?: (queueId: string) => void;
  showActions?: boolean;
}

export function QueueTicketCard({
  ticket,
  onCall,
  onMarkArrival,
  onComplete,
  onCancel,
  showActions = true,
}: QueueTicketCardProps) {
  const [serviceName, setServiceName] = useState<string | null>(null);
  const [barberName, setBarberName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Load service and barber names
  useEffect(() => {
    const loadNames = async () => {
      setLoading(true);
      try {
        // Load service name
        if (ticket.serviceId) {
          const serviceDoc = await getDoc(doc(db, 'services', ticket.serviceId));
          if (serviceDoc.exists()) {
            const service = serviceDoc.data() as Service;
            setServiceName(service.name);
          }
        }

        // Load barber name
        if (ticket.barberId) {
          const barberDoc = await getDoc(doc(db, 'barbers', ticket.barberId));
          if (barberDoc.exists()) {
            const barber = barberDoc.data() as Barber;
            setBarberName(barber.displayName);
          }
        }
      } catch (error) {
        console.error('Error loading service/barber names:', error);
      } finally {
        setLoading(false);
      }
    };

    loadNames();
  }, [ticket.serviceId, ticket.barberId]);

  const formatTime = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
  };

  const canCall = ticket.status === 'waiting';
  const canMarkArrival = ticket.status === 'notified';
  const canComplete = ticket.status === 'arrived' || ticket.status === 'in_service';
  const canCancel = ['waiting', 'notified', 'arrived'].includes(ticket.status);

  return (
    <div className="bg-surface rounded-lg shadow-md p-6 border border-hairline hover:shadow-lg transition-shadow">
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-2xl font-bold text-ink">#{ticket.ticketNumber}</h3>
          <p className="text-sm text-ink-subtle">{formatDate(ticket.createdAt)}</p>
        </div>
        <QueueStatusBadge status={ticket.status} size="lg" />
      </div>

      {/* Position & Time */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-info/10 rounded-lg p-3 text-center">
          <div className="text-sm text-ink-muted">Position</div>
          <div className="text-3xl font-bold text-info">{(ticket.status === 'in_service' || ticket.status === 'finishing') ? '✂️' : ticket.position}</div>
        </div>
        <div className="bg-accent/10 rounded-lg p-3 text-center">
          <div className="text-sm text-ink-muted">Est. Wait</div>
          <div className="text-2xl font-bold text-accent">{formatWaitTime(ticket.estimatedWaitTime || 0)}</div>
        </div>
      </div>

      {/* Details */}
      <div className="space-y-2 mb-4 text-sm">
        {ticket.serviceId && (
          <div className="flex justify-between">
            <span className="text-ink-muted">Service:</span>
            <span className="font-medium text-ink-muted">
              {loading ? '...' : serviceName || ticket.serviceId}
            </span>
          </div>
        )}
        {ticket.barberId && (
          <div className="flex justify-between">
            <span className="text-ink-muted">Barber:</span>
            <span className="font-medium text-ink-muted">
              {loading ? '...' : barberName || ticket.barberId}
            </span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-ink-muted">Created:</span>
          <span className="font-medium text-ink-muted">{formatTime(ticket.createdAt)}</span>
        </div>
        {ticket.arrivedAt && (
          <div className="flex justify-between">
            <span className="text-ink-muted">Arrived:</span>
            <span className="font-medium text-ink-muted">{formatTime(ticket.arrivedAt)}</span>
          </div>
        )}
        {ticket.calledAt && (
          <div className="flex justify-between">
            <span className="text-ink-muted">Called:</span>
            <span className="font-medium text-ink-muted">{formatTime(ticket.calledAt)}</span>
          </div>
        )}
        {ticket.completedAt && (
          <div className="flex justify-between">
            <span className="text-ink-muted">Completed:</span>
            <span className="font-medium text-ink-muted">{formatTime(ticket.completedAt)}</span>
          </div>
        )}
        {ticket.cancelReason && (
          <div className="flex justify-between">
            <span className="text-ink-muted">Cancel Reason:</span>
            <span className="font-medium text-danger">{ticket.cancelReason}</span>
          </div>
        )}
      </div>

      {/* Actions */}
      {showActions && (
        <div className="flex flex-wrap gap-2 pt-4 border-t border-hairline">
          {canCall && onCall && (
            <Button
              size="sm"
              variant="primary"
              onClick={() => onCall(ticket.queueId)}
              className="flex-1"
            >
              🔔 Call
            </Button>
          )}
          {canMarkArrival && onMarkArrival && (
            <Button
              size="sm"
              variant="success"
              onClick={() => onMarkArrival(ticket.queueId)}
              className="flex-1"
            >
              ✅ Mark Arrival
            </Button>
          )}
          {canComplete && onComplete && (
            <Button
              size="sm"
              variant="success"
              onClick={() => onComplete(ticket.queueId)}
              className="flex-1"
            >
              🎉 Complete
            </Button>
          )}
          {canCancel && onCancel && (
            <Button
              size="sm"
              variant="danger"
              onClick={() => onCancel(ticket.queueId)}
              className="flex-1"
            >
              ❌ Cancel
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
